import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Printer } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { fmtDate, isExpired, renderQuoteHtml } from '../utils/quotes';

// Página pública de una cotización: la abre el cliente desde el enlace que le llega por WhatsApp (sin iniciar sesión).
// La base solo entrega esa cotización (si ya está aprobada o enviada) y nada del teléfono ni del correo del cliente.
export default function PublicQuotePage() {
  const { token } = useParams();
  const frame = useRef(null);
  const [state, setState] = useState({ loading: true, data: null, failed: false });

  useEffect(() => {
    let alive = true;
    setState({ loading: true, data: null, failed: false });
    supabase.rpc('public_quote', { p_token: token || '' }).then(({ data, error }) => {
      if (alive) setState({ loading: false, data: error ? null : data, failed: Boolean(error) });
    });
    return () => { alive = false; };
  }, [token]);

  // El enlace es privado: que los buscadores no lo indexen.
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots'; meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => { meta.remove(); };
  }, []);

  const { data } = state;
  const html = useMemo(() => (data?.quote ? renderQuoteHtml({ quote: data.quote, items: data.items || [], customer: data.customer, business: data.business, settings: data.settings }) : ''), [data]);
  useEffect(() => {
    if (data?.quote) document.title = `Cotización ${data.quote.number}`;
  }, [data]);

  const print = () => {
    const w = frame.current?.contentWindow;
    if (w) { w.focus(); w.print(); }
  };

  const shell = (children) => (
    <div className="min-h-screen bg-slate-100 text-slate-900 flex flex-col items-center px-4 py-6 gap-4">{children}</div>
  );

  if (state.loading) return shell(<p className="text-sm text-slate-600 mt-10">Cargando la cotización…</p>);
  if (state.failed) return shell(<p className="text-sm text-slate-700 mt-10">No se pudo abrir la cotización. Revisa tu conexión e inténtalo de nuevo.</p>);
  if (!data) return shell(<p className="text-sm text-slate-700 mt-10 text-center max-w-md">Este enlace no es válido o la cotización ya no está disponible. Pídele a quien te la envió que te la comparta de nuevo.</p>);
  if (data.reemplazada) {
    return shell(
      <div className="mt-10 text-center max-w-md space-y-3 text-sm text-slate-700">
        <p>La cotización {data.numero} fue actualizada y esta versión ya no es la vigente.</p>
        {data.nueva ? <Link to={`/cotizacion/${data.nueva}`} className="inline-block bg-slate-900 text-white font-bold px-4 py-2 rounded-xl">Ver la versión vigente</Link> : <p>Pídele a quien te la envió la versión nueva.</p>}
      </div>
    );
  }

  const q = data.quote;
  const expired = isExpired(q);
  return shell(
    <>
      <div className="w-full max-w-4xl flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-extrabold">Cotización {q.number}{q.version > 1 ? ` · versión ${q.version}` : ''}</h1>
          <p className="text-xs text-slate-600">
            {data.business?.nombre_negocio || data.settings?.legal_name || ''}
            {q.status === 'aceptada' ? ' · Aceptada' : q.valid_until ? ` · Vigente hasta el ${fmtDate(q.valid_until)}` : ''}
          </p>
        </div>
        <button type="button" onClick={print} className="bg-slate-900 text-white text-xs font-bold px-4 py-2 rounded-xl flex items-center gap-1.5"><Printer size={14} /> Descargar PDF / Imprimir</button>
      </div>
      {expired && <div className="w-full max-w-4xl p-3 rounded-xl border border-orange-300 bg-orange-50 text-xs text-orange-800">Esta cotización venció el {fmtDate(q.valid_until)}. Escríbenos y te la actualizamos.</div>}
      <iframe ref={frame} title={`Cotización ${q.number}`} srcDoc={html} sandbox="allow-same-origin allow-modals" className="w-full max-w-4xl h-[80vh] bg-white rounded-xl border border-slate-300" />
    </>
  );
}
